import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Wifi, Zap, Shield, Clock, Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const InternetZuhausePage: React.FC = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://files.check24.net/widgets/auto/380069/c24pp-dsl-iframe/dsl-iframe.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Internet zuhause Völklingen & Saarland - DSL & Glasfaser vergleichen | Easy Vertrag</title>
        <meta name="description" content="Internet-Anbieter in Völklingen und dem Saarland vergleichen – DSL, Glasfaser & Kabel von Telekom, Vodafone, O2. Persönliche Beratung in Völklingen. Jetzt wechseln!" />
        <meta name="keywords" content="Internet Völklingen, DSL Saarland, Glasfaser Völklingen, Internet Anbieter Saarland, Internet Saarbrücken, DSL Tarife vergleichen" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/internet-zuhause" />
        <meta property="og:title" content="Internet zuhause - DSL & Kabel vergleichen | Easy Vertrag" />
        <meta property="og:description" content="Vergleichen Sie alle DSL, Kabel und Glasfaser-Tarife. Bis zu 1000 Mbit/s für zuhause zum besten Preis." />
        <meta property="og:url" content="https://easy4vertrag.de/internet-zuhause" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Internet zuhause", "item": "https://easy4vertrag.de/internet-zuhause"}
            ]
          }
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <Wifi className="w-8 h-8" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Internet zuhause</h1>
            <p className="text-xl text-blue-100">
              Finden Sie den perfekten DSL- oder Kabel-Tarif für Ihr Zuhause
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-blue-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wifi className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Schnelles Internet</h3>
              <p className="text-gray-600">
                DSL, Kabel und Glasfaser mit bis zu 1000 Mbit/s
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-green-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Alle Anbieter</h3>
              <p className="text-gray-600">
                Vergleichen Sie Tarife von Telekom, Vodafone, O2 und mehr
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-purple-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Beste Preise</h3>
              <p className="text-gray-600">
                Exklusive Online-Angebote und Cashback-Aktionen
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-orange-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Schneller Wechsel</h3>
              <p className="text-gray-600">
                Einfacher Online-Abschluss und schnelle Aktivierung
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DSL Comparison Widget */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="w-full max-w-none lg:max-w-[95vw] xl:max-w-[95vw] mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 bg-blue-600 text-white">
                <h2 className="text-2xl font-bold mb-2">DSL & Internet Vergleich</h2>
                <p>Finden Sie den perfekten Internet-Tarif für Ihr Zuhause</p>
              </div>
              <div className="p-2 md:p-4 lg:p-6">
                <div style={{ width: '100%' }} id="c24pp-dsl-iframe"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Warum Internet zuhause bei Easy Vertrag?
            </h2>
            <p className="text-gray-600 mb-6">
              Wir helfen Ihnen dabei, den perfekten Internet-Tarif für Ihr Zuhause zu finden.
              Vergleichen Sie alle verfügbaren Anbieter wie Telekom, Vodafone, O2, Unitymedia und profitieren Sie von exklusiven Online-Angeboten.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                'Glasfaser bis 1000 Mbit/s verfügbar',
                'DSL ab 9,99 €/Monat im ersten Jahr',
                'Kein Techniker-Termin nötig bei VDSL',
                'Monatlich kündbare Tarife verfügbar',
                'Inklusive Router bei vielen Anbietern',
                'Alle Anbieter in Ihrer Region vergleichen',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tariff Types Explained */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">DSL, Kabel oder Glasfaser - Was ist besser?</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  type: 'DSL / VDSL',
                  speed: 'bis 250 Mbit/s',
                  desc: 'Klassischer Telefonanschluss. Verfügbar fast überall in Deutschland. Günstig und zuverlässig für normalen Haushaltsbedarf.',
                  pros: ['Weit verbreitet', 'Günstige Preise', 'Einfache Installation'],
                  color: 'bg-blue-50 border-blue-200',
                },
                {
                  type: 'Kabelnetz',
                  speed: 'bis 1000 Mbit/s',
                  desc: 'Über das Kabelfernsehnetz. Hohe Geschwindigkeiten zu guten Preisen - besonders in Städten verbreitet.',
                  pros: ['Sehr hohe Speeds', 'Oft günstiger als Glasfaser', 'TV-Kombi möglich'],
                  color: 'bg-green-50 border-green-200',
                },
                {
                  type: 'Glasfaser (FTTH)',
                  speed: 'bis 10.000 Mbit/s',
                  desc: 'Zukunftssicherste Technologie - direkte Glasfaserleitung bis in die Wohnung. Verfügbarkeit noch begrenzt.',
                  pros: ['Maximale Geschwindigkeit', 'Stabile Verbindung', 'Zukunftssicher'],
                  color: 'bg-orange-50 border-orange-200',
                },
              ].map((item, i) => (
                <div key={i} className={`${item.color} border rounded-xl p-5`}>
                  <h3 className="font-bold text-gray-800 mb-1">{item.type}</h3>
                  <p className="text-sm font-medium text-blue-600 mb-2">{item.speed}</p>
                  <p className="text-gray-600 text-sm mb-3">{item.desc}</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {item.pros.map((pro, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />{pro}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Häufige Fragen zu Internet zuhause</h2>
            <div className="space-y-3">
              {[
                {
                  q: 'Was ist der günstigste Internet-Tarif in Deutschland?',
                  a: 'DSL-Tarife beginnen ab ca. 10-15 €/Monat in der Aktionsphase. Kabel-Internet ist oft etwas teurer, aber schneller. Vergleichen Sie immer den Normalpreis nach der Aktionsphase, da dieser oft viel höher ist.',
                },
                {
                  q: 'Kann ich meinen Internetanbieter wechseln?',
                  a: 'Ja! Kündigen Sie Ihren alten Vertrag rechtzeitig (meist 3 Monate vor Ablauf der Mindestlaufzeit) und schließen Sie beim neuen Anbieter an. Viele Anbieter übernehmen sogar die Umzugskosten.',
                },
                {
                  q: 'Wie lange dauert die Einrichtung eines neuen Internetzugangs?',
                  a: 'Bei DSL/Kabel dauert die Schaltung meist 2-4 Wochen. Bei Glasfaser kann es länger dauern, da oft Kabel verlegt werden müssen. Sie erhalten immer einen genauen Termin.',
                },
              ].map((item, i) => (
                <details key={i} className="bg-gray-50 rounded-lg group">
                  <summary className="font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center p-5">
                    {item.q}
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="px-5 pb-5 text-gray-600">{item.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/kontakt" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                Persönliche Beratung anfordern <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternetZuhausePage;